import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Ticket, Sparkles, Crown } from "lucide-react";

const TIER_LABEL = {
  spectator: { label: "SPECTATOR", icon: Ticket, color: "text-white/70" },
  resident: { label: "RESIDENT", icon: Sparkles, color: "text-[#00E5FF]" },
  vip_immortal: { label: "VIP • IMMORTAL", icon: Crown, color: "text-[#FF007F]" },
};

export default function MyReservations() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/my-reservations");
        setRows(data);
      } catch (e) {
        toast.error("Could not load reservations.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const upgrade = async (tier) => {
    setUpgrading(tier);
    try {
      const { data } = await http.post("/payments/checkout", {
        tier,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setUpgrading(null);
    }
  };

  const tierInfo = TIER_LABEL[user?.subscription_tier || "spectator"];
  const TierIcon = tierInfo.icon;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <Link to="/hologram-club" className="inline-flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/60 hover:text-[#00E5FF]" data-testid="myres-back">
            <ArrowLeft size={12} /> Back to Club
          </Link>
          <button
            onClick={logout}
            className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/60 hover:text-[#FF007F] inline-flex items-center gap-2"
            data-testid="myres-logout"
          >
            <LogOut size={12} /> Log Out
          </button>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Profile card */}
          <aside className="md:col-span-4">
            <div className="card-surface gradient-border p-6">
              <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">Your Profile</div>
              <div className="mt-3 font-display font-bold text-2xl" data-testid="myres-name">{user?.name}</div>
              <div className="font-mono-ui text-[11px] tracking-[0.15em] text-white/50 mt-1" data-testid="myres-email">{user?.email}</div>

              <div className={`mt-6 inline-flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2 ${tierInfo.color}`}>
                <TierIcon size={14} />
                <span className="font-mono-ui text-[11px] tracking-[0.25em] uppercase" data-testid="myres-tier">{tierInfo.label}</span>
              </div>

              {user?.subscription_tier !== "vip_immortal" && (
                <div className="mt-6 space-y-2">
                  <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/40">Upgrade</div>
                  {user?.subscription_tier !== "resident" && (
                    <button
                      onClick={() => upgrade("resident")}
                      disabled={upgrading === "resident"}
                      data-testid="upgrade-resident"
                      className="btn-ghost w-full disabled:opacity-60"
                    >
                      {upgrading === "resident" ? "Redirecting..." : "Go Resident · $49/mo"}
                    </button>
                  )}
                  <button
                    onClick={() => upgrade("vip_immortal")}
                    disabled={upgrading === "vip_immortal"}
                    data-testid="upgrade-vip"
                    className="btn-neon w-full disabled:opacity-60"
                  >
                    {upgrading === "vip_immortal" ? "Redirecting..." : "Go VIP • Immortal · $499/mo"}
                  </button>
                </div>
              )}
            </div>
          </aside>

          {/* Reservations list */}
          <main className="md:col-span-8">
            <div className="flex items-end justify-between">
              <div>
                <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">Reservations</div>
                <h1 className="mt-2 font-display font-black tracking-tighter text-4xl md:text-5xl">Your Nights.</h1>
              </div>
              <Link to="/hologram-club#schedule" className="btn-ghost text-[10px] py-2.5 px-4" data-testid="myres-book-new">
                Book Another
              </Link>
            </div>

            {loading ? (
              <div className="mt-10 font-mono-ui text-xs text-white/50">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="mt-10 border border-white/10 bg-[#0B0C10] p-10 text-center" data-testid="myres-empty">
                <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-white/40">No reservations yet</div>
                <div className="mt-3 font-display text-xl">The door is open. Come pick a night.</div>
                <Link to="/hologram-club#schedule" className="btn-neon inline-block mt-6" data-testid="myres-empty-cta">
                  See Tonight's Lineup
                </Link>
              </div>
            ) : (
              <ul className="mt-8 space-y-3" data-testid="myres-list">
                {rows.map((r) => (
                  <li key={r.id} className="card-surface p-5 flex flex-wrap items-center gap-4 justify-between" data-testid={`myres-row-${r.id}`}>
                    <div>
                      <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[#00E5FF]">
                        {r.venue === "la" ? "Los Angeles" : "Las Vegas"} · {r.date}
                      </div>
                      <div className="mt-1 font-display font-bold text-lg">Show: {r.show_id.replace(/^show-/, "").replace(/-la$/, "").toUpperCase()}</div>
                      <div className="font-mono-ui text-[11px] text-white/50">{r.guests} guest{r.guests > 1 ? "s" : ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-white/40">Code</div>
                      <div className="font-display font-black text-xl text-cyan-glow">{r.confirmation_code}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
